import {useCallback, useEffect, useRef, useState} from 'react';
import useRole from '../../hooks/useRole';
import {getUserRole} from '../../store/AuthSlice';
import {useAppSelector} from '../../store/Store';
import {ApiResponse, SectionData} from '../types/AiInsights';
import {useOktaAuth} from '@okta/okta-react';
import {generateAiInsights} from '../../components/AiInsights/AiInsightsApi';
import {API_TIMEOUT_MS} from '../constants/time';

const INSIGHTS_BUSY_ERROR =
  'Apologies, the server is currently busy. Please try again shortly.';

const parseMarkdownSections = (
  markdown: string,
): {title: string; introContent: string; sections: SectionData[]} => {
  const sections: SectionData[] = [];
  let title = '';
  let introContent = '';

  // Extract main title (#)
  const titleRegex = /^#\s+(.+)/m;
  const titleMatch = titleRegex.exec(markdown);
  if (titleMatch) {
    title = titleMatch[1].trim();
  }

  const withoutTitle = markdown.replace(/^#\s+.+\n/m, '');

  const introRegex = /^([\s\S]*?)^##\s+/m;
  const introMatch = introRegex.exec(withoutTitle);
  if (introMatch) {
    introContent = introMatch[1].trim();
  }

  // Split by ## (section headers only)
  const parts = withoutTitle.split(/^##\s+/m);

  for (let i = 1; i < parts.length; i++) {
    if (!parts[i] || parts[i].trim() === '') continue;

    const lines = parts[i].split('\n');
    const header = lines[0]?.trim() || `Section ${i}`;
    const content = lines.slice(1).join('\n').trim();

    if (header) {
      sections.push({
        header,
        content,
      });
    }
  }

  return {title, introContent, sections};
};

function cleanMarkdown(raw: string): string {
  let cleaned = raw;
  const startText = '```markdown';
  const endText = '```';

  if (cleaned.startsWith(startText)) {
    cleaned = cleaned.slice(startText.length);
  }
  if (cleaned.endsWith(endText)) {
    cleaned = cleaned.slice(0, -endText.length);
  }

  return cleaned.trim();
}

interface InsightVisualizerProps {
  module?: string;
}

function useInsights({module = ''}: InsightVisualizerProps) {
  const userRole = useAppSelector(getUserRole);
  const {isManagerRole, currentRole} = useRole(
    userRole?.[userRole?.length - 1] || '',
  );
  const [title, setTitle] = useState<string>('');
  const [introContent, setIntroContent] = useState<string>('');
  const [sections, setSections] = useState<SectionData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const {authState} = useOktaAuth();
  const claimsData = authState?.accessToken?.claims;
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [expandedSections, setExpandedSections] = useState<string[]>([]);
  const [expandedSectionsConfig, setExpandedSectionsConfig] = useState<
    string[]
  >([]);

  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchInsights = useCallback(async () => {
    if (!module) return;
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;
    setLoading(true);
    setSections([]);
    setTitle('');
    setIntroContent('');
    setError('');

    timeoutRef.current = setTimeout(() => {
      abortControllerRef.current?.abort();
      setError(INSIGHTS_BUSY_ERROR);
      setLoading(false);
    }, API_TIMEOUT_MS);
    try {
      const data: ApiResponse = await generateAiInsights(
        claimsData,
        isManagerRole,
        module,
        controller.signal,
        [currentRole],
      );
      const cleanedMarkdown = cleanMarkdown(data?.result);
      const {
        title: parsedTitle,
        introContent: parsedIntro,
        sections: parsedSections,
      } = parseMarkdownSections(cleanedMarkdown);

      // 200 OK with an error string in `result` (no markdown title/sections)
      // — surface it as an error instead of silently rendering empty content.
      if (!parsedTitle && parsedSections.length === 0) {
        const errorText = (parsedIntro || cleanedMarkdown).trim();
        setError(errorText || INSIGHTS_BUSY_ERROR);
        setTitle('');
        setIntroContent('');
        setSections([]);
        setExpandedSectionsConfig([]);
        setExpandedSections([]);
        return;
      }

      setTitle(parsedTitle);
      setIntroContent(parsedIntro);
      setSections(parsedSections);
      setError('');

      let expandedSectionsFromBackend: string[] = [];

      if (
        data?.section_config &&
        Array.isArray(data.section_config) &&
        data.section_config.length > 0
      ) {
        const existingSectionHeaders = new Set(
          parsedSections.map((s) => s.header),
        );
        expandedSectionsFromBackend = data.section_config
          .filter(
            (config) =>
              config.section_expand &&
              existingSectionHeaders.has(config.section_header),
          )
          .map((config) => config.section_header);
      } else if (
        data?.expandedSections &&
        Array.isArray(data.expandedSections)
      ) {
        expandedSectionsFromBackend = data.expandedSections;
      }

      setExpandedSectionsConfig(expandedSectionsFromBackend);
      setExpandedSections(expandedSectionsFromBackend);
    } catch (err: unknown) {
      // Only show error if it's not an abort error (timeout)
      if (err instanceof Error && err.name !== 'AbortError') {
        console.error('Error fetching insights:', err);
        setError(INSIGHTS_BUSY_ERROR);
      }
    } finally {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      setLoading(false);
    }
  }, [claimsData, currentRole, isManagerRole, module]);

  useEffect(() => {
    fetchInsights();
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [fetchInsights]);

  return {
    refetch: fetchInsights,
    title,
    introContent,
    sections,
    expandedSectionsConfig,
    loading,
    expandedSections,
    error,
  };
}

export default useInsights;
